export type ProfitMarginInputs = {
  monthlyUnits: number;
  otherCost: number;
  sellingPrice: number;
  shippingCost: number;
  targetMarginPercent: number;
  transactionFeePercent: number;
  unitCost: number;
};

export type ProfitMarginResult = {
  /** Selling price that leaves exactly zero net profit. Null when fees swallow the whole price. */
  breakEvenPrice: number | null;
  grossMarginPercent: number;
  grossProfit: number;
  monthlyGrossProfit: number;
  monthlyNetProfit: number;
  monthlyRevenue: number;
  /** Gross profit over unit cost — the number people confuse with margin. Null when unit cost is zero. */
  markupPercent: number | null;
  netMarginPercent: number;
  netProfit: number;
  /** Net profit over total cost per unit. Null when there is no cost to return against. */
  roiPercent: number | null;
  /** Price required to hit the target net margin. Null when the target is unreachable at these costs. */
  targetPrice: number | null;
  totalCostPerUnit: number;
  transactionFee: number;
};

const PERCENT_FIELDS = new Set<keyof ProfitMarginInputs>([
  'targetMarginPercent',
  'transactionFeePercent',
]);

export function normalizeProfitMarginInput(
  key: keyof ProfitMarginInputs,
  value: string,
) {
  const numericValue = Number.parseFloat(value);

  if (!Number.isFinite(numericValue)) {
    return value;
  }

  if (numericValue < 0) {
    return '0';
  }

  // A 100% net margin needs zero cost, so the target is capped just below it to
  // keep the reverse-priced figure finite and honest.
  if (key === 'targetMarginPercent' && numericValue > 99) {
    return '99';
  }

  if (PERCENT_FIELDS.has(key) && numericValue > 100) {
    return '100';
  }

  if (key === 'monthlyUnits') {
    return String(Math.floor(numericValue));
  }

  return value;
}

function asNonNegative(value: number) {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

function asPercent(value: number) {
  return Math.min(100, asNonNegative(value));
}

function roundCurrency(value: number) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function roundPercent(value: number) {
  return Math.round((value + Number.EPSILON) * 10) / 10;
}

export function calculateProfitMargin(
  input: ProfitMarginInputs,
): ProfitMarginResult {
  const sellingPrice = asNonNegative(input.sellingPrice);
  const unitCost = asNonNegative(input.unitCost);
  const shippingCost = asNonNegative(input.shippingCost);
  const otherCost = asNonNegative(input.otherCost);
  const monthlyUnits = asNonNegative(input.monthlyUnits);
  const transactionFeePercent = asPercent(input.transactionFeePercent);
  const targetMarginPercent = Math.min(
    99,
    asNonNegative(input.targetMarginPercent),
  );

  const feeRate = transactionFeePercent / 100;
  const transactionFee = roundCurrency(sellingPrice * feeRate);

  // Costs that are fixed per unit, before the percentage fee is taken out of
  // whatever the item sells for. Both reverse calculations solve against this.
  const fixedCostPerUnit = roundCurrency(unitCost + shippingCost + otherCost);

  const grossProfit = roundCurrency(sellingPrice - unitCost);
  const grossMarginPercent =
    sellingPrice > 0 ? roundPercent((grossProfit / sellingPrice) * 100) : 0;

  const totalCostPerUnit = roundCurrency(fixedCostPerUnit + transactionFee);
  const netProfit = roundCurrency(sellingPrice - totalCostPerUnit);
  const netMarginPercent =
    sellingPrice > 0 ? roundPercent((netProfit / sellingPrice) * 100) : 0;

  const markupPercent =
    unitCost > 0 ? roundPercent((grossProfit / unitCost) * 100) : null;

  const roiPercent =
    totalCostPerUnit > 0
      ? roundPercent((netProfit / totalCostPerUnit) * 100)
      : null;

  const breakEvenPrice =
    feeRate < 1 ? roundCurrency(fixedCostPerUnit / (1 - feeRate)) : null;

  const targetDenominator = 1 - feeRate - targetMarginPercent / 100;
  const targetPrice =
    targetDenominator > 0
      ? roundCurrency(fixedCostPerUnit / targetDenominator)
      : null;

  return {
    breakEvenPrice,
    grossMarginPercent,
    grossProfit,
    monthlyGrossProfit: roundCurrency(grossProfit * monthlyUnits),
    monthlyNetProfit: roundCurrency(netProfit * monthlyUnits),
    monthlyRevenue: roundCurrency(sellingPrice * monthlyUnits),
    markupPercent,
    netMarginPercent,
    netProfit,
    roiPercent,
    targetPrice,
    totalCostPerUnit,
    transactionFee,
  };
}
